import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  PHASE_FILL,
  PHASE_LEGEND,
  PHASE_SWATCH,
  buildChartModel,
  fmtMem,
  fmtPct,
  fmtSpanMs,
  memSummary,
  noDataText,
  parseMemTelemetry,
  phaseRows,
  type MemLevel,
  type MemSummaryFields,
  type MemTelemetry,
} from "@/lib/memTelemetry";

const LEVEL_TEXT: Record<MemLevel, string> = {
  ok: "text-foreground",
  warn: "text-amber-600 dark:text-amber-400",
  red: "text-red-600 dark:text-red-400 font-semibold",
};

/** Compact "Peak RSS" for a job row (registry/publish builds, sibling
 *  lists): value, which process, % of the guest's memory — red above 85%.
 *  Nothing when the job has no data and is not finished. */
export function PeakRss({
  job,
  className,
}: {
  job: MemSummaryFields & { status?: string };
  className?: string;
}) {
  const s = memSummary(job);
  if (!s) {
    if (job.status === "queued" || job.status === "running") return null;
    return (
      <span
        className={cn("text-xs text-muted-foreground", className)}
        title="This job's runner predates the memory sampler, or the job never ran in a VM"
      >
        peak RSS —
      </span>
    );
  }
  return (
    <span
      className={cn("text-xs tabular-nums", LEVEL_TEXT[s.level], className)}
      data-testid="peak-rss"
      data-level={s.level}
      title={
        `Peak guest memory ${fmtMem(s.peakMb)}` +
        (s.process
          ? `; largest process ${s.process} ${fmtMem(s.processMb)}`
          : "") +
        (s.guestMb ? `; guest has ${fmtMem(s.guestMb)}` : "") +
        (s.complete
          ? ""
          : "; INCOMPLETE — the guest ended without a final report")
      }
    >
      peak RSS {fmtMem(s.peakMb)}
      {s.pct != null && <> ({fmtPct(s.pct)})</>}
      {s.process && (
        <span className="text-muted-foreground"> · {s.process}</span>
      )}
      {!s.complete && (
        <span title="guest ended without a final report"> ⚠</span>
      )}
    </span>
  );
}

function useWidth(
  ref: React.RefObject<HTMLElement | null>,
  fallback = 640,
): number {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () =>
      setW(Math.round(el.getBoundingClientRect().width) || fallback);
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return w;
}

/** The SVG: RSS per sample (line), build-tree RSS (dashed), phase bands
 *  behind, the guest's MemTotal as a limit line, the 85% line, the peak
 *  marked. Pure SVG, theme tokens only (light/dark), sized to its container
 *  so text stays readable at phone width. */
export function MemoryChart({ telemetry }: { telemetry: MemTelemetry }) {
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const height = width < 420 ? 200 : 250;
  const m = useMemo(
    () => buildChartModel(telemetry, { width, height }),
    [telemetry, width, height],
  );
  const peakColor =
    m.peak.level === "red"
      ? "fill-red-600 stroke-red-600 dark:fill-red-400 dark:stroke-red-400"
      : m.peak.level === "warn"
        ? "fill-amber-600 stroke-amber-600 dark:fill-amber-400 dark:stroke-amber-400"
        : "fill-primary stroke-primary";
  const labelRight = m.peak.x < m.plot.x + m.plot.w * 0.55;
  const clipId = "memchart-clip";
  return (
    <div ref={box} className="w-full" data-testid="memory-chart">
      <svg
        width={m.width}
        height={m.height}
        viewBox={`0 0 ${m.width} ${m.height}`}
        role="img"
        aria-label={`Memory over the build: ${m.peak.label}`}
        className="block max-w-full text-muted-foreground"
      >
        <defs>
          <clipPath id={clipId}>
            <rect
              x={m.plot.x}
              y={m.plot.y}
              width={m.plot.w}
              height={m.plot.h}
            />
          </clipPath>
        </defs>
        {/* phase bands */}
        <g clipPath={`url(#${clipId})`}>
          {m.bands.map((b, i) => (
            <g key={i}>
              <rect
                x={b.x}
                y={m.plot.y}
                width={b.w}
                height={m.plot.h}
                className={PHASE_FILL[b.kind]}
              >
                <title>{b.title}</title>
              </rect>
              {i > 0 && (
                <line
                  x1={b.x}
                  x2={b.x}
                  y1={m.plot.y}
                  y2={m.plot.y + m.plot.h}
                  className="stroke-border"
                  strokeWidth={1}
                />
              )}
              {b.showLabel && (
                <text
                  x={b.x + 3}
                  y={m.plot.y + 10}
                  fontSize={9}
                  className="fill-muted-foreground"
                >
                  {b.name
                    .replace(/^Stage \d+: /, "")
                    .slice(0, Math.floor(b.w / 5.2))}
                </text>
              )}
            </g>
          ))}
        </g>
        {/* grid + y axis */}
        {m.yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={m.plot.x}
              x2={m.plot.x + m.plot.w}
              y1={t.pos}
              y2={t.pos}
              className="stroke-border"
              strokeWidth={0.5}
            />
            <text
              x={m.plot.x - 6}
              y={t.pos + 3}
              fontSize={10}
              textAnchor="end"
              className="fill-muted-foreground"
            >
              {t.label}
            </text>
          </g>
        ))}
        {m.xTicks.map((t, i) => (
          <text
            key={i}
            x={t.pos}
            y={m.plot.y + m.plot.h + 15}
            fontSize={10}
            textAnchor="middle"
            className="fill-muted-foreground"
          >
            {t.label}
          </text>
        ))}
        {/* limit (guest MemTotal) and the 85% line */}
        {m.limitY != null && (
          <g>
            <line
              x1={m.plot.x}
              x2={m.plot.x + m.plot.w}
              y1={m.limitY}
              y2={m.limitY}
              className="stroke-foreground"
              strokeWidth={1.25}
            />
            <text
              x={m.plot.x + m.plot.w - 2}
              y={m.limitY - 3}
              fontSize={9}
              textAnchor="end"
              className="fill-foreground"
            >
              guest memory {fmtMem(telemetry.guest_mem_mb)}
            </text>
          </g>
        )}
        {m.redY != null && (
          <line
            x1={m.plot.x}
            x2={m.plot.x + m.plot.w}
            y1={m.redY}
            y2={m.redY}
            className="stroke-red-500/70"
            strokeWidth={1}
            strokeDasharray="4 3"
          >
            <title>85% of guest memory</title>
          </line>
        )}
        {/* series */}
        <g clipPath={`url(#${clipId})`}>
          {m.treePath && m.dots.length > 1 && (
            <path
              d={m.treePath}
              fill="none"
              className="stroke-muted-foreground"
              strokeWidth={1.25}
              strokeDasharray="3 3"
            />
          )}
          {m.usedPath && m.dots.length > 1 && (
            <path
              d={m.usedPath}
              fill="none"
              className="stroke-primary"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          )}
        </g>
        {m.dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={3} className="fill-primary">
            <title>{d.title}</title>
          </circle>
        ))}
        {/* the guest ended here */}
        {m.incomplete && (
          <g>
            <line
              x1={m.endX}
              x2={m.endX}
              y1={m.plot.y}
              y2={m.plot.y + m.plot.h}
              className="stroke-red-500"
              strokeWidth={1.5}
              strokeDasharray="2 2"
            />
            <text
              x={m.endX - 4}
              y={m.plot.y + m.plot.h - 6}
              fontSize={9}
              textAnchor="end"
              className="fill-red-600 dark:fill-red-400"
            >
              guest ended
            </text>
          </g>
        )}
        {/* peak */}
        <g>
          <line
            x1={m.plot.x}
            x2={m.plot.x + m.plot.w}
            y1={m.peak.y}
            y2={m.peak.y}
            className={peakColor}
            strokeWidth={0.75}
            strokeDasharray="1 3"
          />
          <circle
            cx={m.peak.x}
            cy={m.peak.y}
            r={5}
            className={peakColor}
            fillOpacity={0.9}
            data-testid="peak-marker"
          >
            <title>
              {m.peak.label}
              {m.peak.betweenSamples
                ? " (a spike between two samples; the guest tracks the peak every 2 s)"
                : ""}
            </title>
          </circle>
          <text
            x={labelRight ? m.peak.x + 9 : m.peak.x - 9}
            y={Math.max(m.plot.y + 20, m.peak.y - 8)}
            fontSize={10}
            textAnchor={labelRight ? "start" : "end"}
            className={cn(peakColor.split(" ")[0], "stroke-none")}
          >
            {m.peak.label}
          </text>
        </g>
      </svg>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 bg-primary" /> guest used
          memory
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-4 border-t border-dashed border-muted-foreground" />{" "}
          build process tree RSS
        </span>
        {PHASE_LEGEND.filter((l) => m.bands.some((b) => b.kind === l.kind)).map(
          (l) => (
            <span key={l.kind} className="inline-flex items-center gap-1">
              <span
                className={cn(
                  "inline-block h-2.5 w-3 rounded-sm",
                  PHASE_SWATCH[l.kind],
                )}
              />{" "}
              {l.label}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

/** The job detail's Memory section: headline, "incomplete" banner, chart,
 *  per-phase table. `row` is the admin job row (summary columns + the
 *  `mem_telemetry` JSON string). */
export function MemoryPanel({
  row,
}: {
  row: MemSummaryFields & { mem_telemetry?: string | null; status: string };
}) {
  const summary = memSummary(row);
  const telemetry = useMemo(
    () => parseMemTelemetry(row.mem_telemetry),
    [row.mem_telemetry],
  );
  if (!summary) {
    return (
      <div
        className="rounded-md border p-3 text-sm text-muted-foreground"
        data-testid="memory-panel-empty"
      >
        <span className="font-semibold text-xs">Peak RSS</span> —{" "}
        {noDataText(row.status)}
      </div>
    );
  }
  const rows = telemetry ? phaseRows(telemetry) : [];
  return (
    <div
      className={cn(
        "rounded-md border p-3 space-y-3",
        summary.level === "red"
          ? "border-red-500/60 bg-red-500/5"
          : "bg-muted/50",
      )}
      data-testid="memory-panel"
      data-level={summary.level}
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="text-xs font-semibold text-muted-foreground">
          Peak RSS
        </div>
        <div className={cn("text-xl tabular-nums", LEVEL_TEXT[summary.level])}>
          {fmtMem(summary.peakMb)}
          {summary.pct != null && (
            <span className="text-sm ml-1.5">
              ({fmtPct(summary.pct)} of {fmtMem(summary.guestMb)} guest memory)
            </span>
          )}
        </div>
        {summary.process && (
          <div className="text-sm">
            largest process <span className="font-mono">{summary.process}</span>{" "}
            <span className="tabular-nums">{fmtMem(summary.processMb)}</span>
          </div>
        )}
      </div>
      {telemetry?.cold_units && (
        <p className="text-xs" data-testid="cold-units">
          <span className="font-semibold text-muted-foreground">
            Cold cargo units
          </span>{" "}
          <span className="tabular-nums">{telemetry.cold_units.compiled}</span>
          {telemetry.cold_units.fresh != null && (
            <span className="text-muted-foreground">
              {" "}
              compiled, {telemetry.cold_units.fresh} reused from the warm cache
            </span>
          )}
          {telemetry.cold_units.cold.length > 0 && (
            <span className="font-mono">
              : {telemetry.cold_units.cold.join(", ")}
              {telemetry.cold_units.truncated ? ", …" : ""}
            </span>
          )}
          {telemetry.cold_units.dirty?.map((d) => (
            <span
              key={d.unit + d.reason}
              className="block font-mono text-muted-foreground"
            >
              {d.unit}: {d.reason}
            </span>
          ))}
        </p>
      )}
      {summary.level === "red" && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Peak above 85% of the guest's memory — this build was at or near an
          out-of-memory kill.
        </p>
      )}
      {!summary.complete && (
        <p
          className="text-xs rounded border border-amber-500/50 bg-amber-500/10 px-2 py-1"
          data-testid="memory-incomplete"
        >
          Incomplete — the guest ended without a final report (killed, panicked
          or out of memory). The data below is what it streamed before it died
          {telemetry
            ? `, up to ${fmtSpanMs(telemetry.last_t * 1000)} into the build`
            : ""}
          .
        </p>
      )}
      {telemetry ? (
        <>
          <MemoryChart telemetry={telemetry} />
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="text-xs w-full min-w-[420px]">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="font-normal pr-3">Phase</th>
                    <th className="font-normal pr-3">Duration</th>
                    <th className="font-normal pr-3">Peak</th>
                    <th className="font-normal">Largest process</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p, i) => (
                    <tr key={i} className="align-top">
                      <td className="pr-3 py-0.5">
                        <span
                          className={cn(
                            "mr-1.5 inline-block h-2 w-2 rounded-sm",
                            PHASE_SWATCH[p.kind],
                          )}
                        />
                        {p.name}
                        {p.open && !telemetry.complete && (
                          <span className="text-red-600 dark:text-red-400">
                            {" "}
                            (guest ended here)
                          </span>
                        )}
                      </td>
                      <td className="pr-3 py-0.5 tabular-nums">
                        {fmtSpanMs(p.durationMs)}
                      </td>
                      <td
                        className={cn(
                          "pr-3 py-0.5 tabular-nums",
                          LEVEL_TEXT[p.level],
                        )}
                      >
                        {fmtMem(p.peakMb)}
                        {p.pct != null && (
                          <span className="text-muted-foreground">
                            {" "}
                            ({fmtPct(p.pct)})
                          </span>
                        )}
                      </td>
                      <td className="py-0.5 font-mono">
                        {p.process
                          ? `${p.process} ${fmtMem(p.processMb)}`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            One sample per {telemetry.interval_s}s plus the peak (tracked every
            2s in the guest). "Used" is MemTotal − MemAvailable; build tree RSS
            peaked at {fmtMem(telemetry.peak_tree_rss_mb)}
            {telemetry.peak_cgroup_mb
              ? `, build cgroup at ${fmtMem(telemetry.peak_cgroup_mb)}`
              : ""}
            {telemetry.host_peak_mb
              ? `; the VM process on the host peaked at ${fmtMem(telemetry.host_peak_mb)}`
              : ""}
            .
          </p>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          No timeseries was recorded for this job.
        </p>
      )}
    </div>
  );
}
